import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  AnimatePresence,
  motion,
  useScroll,
  useTransform,
  type MotionValue,
} from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { CountUp } from './CountUp';
import { alaViewport } from './alaMotion';
import { plainText } from './propertyUtils';
import aboutUsMapSvg from '../../assets/about-us-map.svg';

/** Schema default; resolved to the bundled asset so it ships with the JS build. */
const MAP_DEFAULT_PATH = '/about-us-map.svg';

const imageDef: SlotGroupDefinition = {
  label: 'Feature Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Modern apartment interior',
    },
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/investors' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'explore our approach' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const statsBgDef: SlotGroupDefinition = {
  label: 'Stats Background',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Aerial community view',
    },
  },
};

const mapDef: SlotGroupDefinition = {
  label: 'US Map',
  fields: {
    image: {
      label: 'Map Image',
      type: 'image',
      defaultValue: '/about-us-map.svg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'American Landmark communities across the United States',
    },
  },
};

const JOURNEY_INTERVAL_MS = 7000;

/** Percent positions calibrated to the contiguous US map artwork. */
const MAP_MARKERS = [
  { state: 'Arizona', x: 22.14, y: 61.22, count: 2 },
  { state: 'Texas', x: 44.65, y: 74.65, count: 42 },
  { state: 'Tennessee', x: 68.5, y: 57.95, count: 4 },
  { state: 'Virginia', x: 81.22, y: 49.22, count: 2 },
  { state: 'North Carolina', x: 81.17, y: 56.31, count: 9 },
  { state: 'South Carolina', x: 79.03, y: 62.77, count: 5 },
  { state: 'Georgia', x: 74.72, y: 68.27, count: 8 },
  { state: 'Florida', x: 77.84, y: 81.79, count: 22 },
] as const;

function ScrollWord({
  progress,
  range,
  className,
  children,
}: {
  progress: MotionValue<number>;
  range: [number, number];
  className?: string;
  children: ReactNode;
}) {
  const scale = useTransform(progress, range, [1.8, 1]);
  const opacity = useTransform(progress, range, [0, 1]);

  return (
    <motion.h2
      style={{ scale, opacity }}
      className={`font-heading text-[clamp(60px,10.42vw,150px)] font-bold leading-[0.9] text-[rgb(var(--ala-text))] ${className || ''}`}
    >
      {children}
    </motion.h2>
  );
}

function MapMarkers() {
  const [active, setActive] = useState<string | null>(null);

  return (
    <div className="pointer-events-none absolute inset-0 z-[999]">
      {MAP_MARKERS.map((marker) => {
        const isActive = active === marker.state;
        return (
          <div
            key={marker.state}
            className="pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${marker.x}%`, top: `${marker.y}%` }}
            onMouseEnter={() => setActive(marker.state)}
            onMouseLeave={() => setActive(null)}
          >
            <button
              type="button"
              aria-label={`${marker.state} — ${marker.count} communities`}
              className="relative flex items-center justify-center"
            >
              <span className="absolute h-4 w-4 animate-ping rounded-full bg-[rgb(var(--ala-accent))]/40" />
              <span className="relative h-3 w-3 rounded-full bg-[rgb(var(--ala-accent))] shadow-md ring-2 ring-white" />
            </button>
            <AnimatePresence>
              {isActive ? (
                <motion.div
                  initial={{ opacity: 0, x: '-50%', y: 4, scale: 0.9 }}
                  animate={{ opacity: 1, x: '-50%', y: 0, scale: 1 }}
                  exit={{ opacity: 0, x: '-50%', y: 4, scale: 0.9 }}
                  transition={{ duration: 0.18 }}
                  className="absolute bottom-full left-1/2 z-[1000] mb-2 whitespace-nowrap"
                >
                  <div className="flex items-center gap-2 rounded-lg bg-[rgb(var(--ala-dark))] px-3 py-2 font-heading text-xs font-semibold text-[rgb(var(--ala-on-primary))] shadow-xl">
                    <span className="h-1.5 w-1.5 rounded-full bg-[rgb(var(--ala-accent))]" />
                    {marker.state}
                    <span className="text-[rgb(var(--ala-on-primary))]/40">•</span>
                    <span>
                      {marker.count}{' '}
                      {Number(marker.count) === 1 ? 'community' : 'communities'}
                    </span>
                  </div>
                  <div className="absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-[rgb(var(--ala-dark))]" />
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

function JourneyCarousel() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const count = 3;

  const goTo = useCallback(
    (next: number) => {
      const normalized = ((next % count) + count) % count;
      if (index === count - 1 && normalized === 0) setDirection(1);
      else if (index === 0 && normalized === count - 1) setDirection(-1);
      else setDirection(normalized > index ? 1 : -1);
      setIndex(normalized);
    },
    [count, index],
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setIndex((prev) => (prev + 1) % count);
    }, JOURNEY_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [count]);

  return (
    <section className="relative w-full overflow-clip bg-[rgb(var(--ala-dark))] px-4 py-20 md:px-8 md:py-32">
      <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-12">
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded bg-[rgb(var(--ala-accent))]" aria-hidden />
            <RichTextContent
              path="aboutcontent.journey_eyebrow"
              label="Journey Eyebrow"
              as="span"
              className="font-heading text-sm font-medium uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-primary))]"
            />
          </div>
          <RichTextContent
            path="aboutcontent.journey_headline"
            label="Journey Headline"
            as="h2"
            className="max-w-[760px] font-heading text-[clamp(33px,3.69vw,60px)] font-medium leading-[1.1] tracking-[-0.05em] text-[rgb(var(--ala-on-primary))]"
          />
        </div>

        <div className="h-[2px] w-full overflow-hidden rounded-full bg-[rgb(var(--ala-on-primary))]/10">
          <motion.div
            key={index}
            className="h-full rounded-full bg-[rgb(var(--ala-secondary))]"
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: JOURNEY_INTERVAL_MS / 1000, ease: 'linear' }}
          />
        </div>

        <div className="relative min-h-[300px] md:min-h-[260px]">
          <AnimatePresence mode="wait" custom={direction}>
            {index === 0 ? (
              <motion.div
                key="journey-0"
                custom={direction}
                initial={{ opacity: 0, x: direction > 0 ? 60 : -60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction > 0 ? -60 : 60 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[clamp(120px,16vw,200px)_1fr] lg:gap-16"
              >
                <RichTextContent
                  path="aboutcontent.journey_1_num"
                  label="Journey 1 Number"
                  as="span"
                  className="font-heading text-[clamp(80px,12vw,180px)] font-bold leading-[0.85] tracking-[-0.04em] text-[rgb(var(--ala-secondary))]/25 [&_p]:text-[rgb(var(--ala-secondary))]/25"
                />
                <div className="flex flex-col gap-6">
                  <RichTextContent
                    path="aboutcontent.journey_1_title"
                    label="Journey 1 Title"
                    as="h3"
                    className="font-heading text-[clamp(24px,2.3vw,40px)] font-medium leading-[1.15] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
                  />
                  <RichTextContent
                    path="aboutcontent.journey_1_body"
                    label="Journey 1 Body"
                    as="p"
                    className="max-w-[640px] font-heading text-[clamp(16px,1.3vw,20px)] font-normal leading-[1.55] tracking-[-0.2px] text-[rgb(var(--ala-on-primary))] [&_p]:text-[rgb(var(--ala-on-primary))]"
                  />
                </div>
              </motion.div>
            ) : null}
            {index === 1 ? (
              <motion.div
                key="journey-1"
                custom={direction}
                initial={{ opacity: 0, x: direction > 0 ? 60 : -60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction > 0 ? -60 : 60 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[clamp(120px,16vw,200px)_1fr] lg:gap-16"
              >
                <RichTextContent
                  path="aboutcontent.journey_2_num"
                  label="Journey 2 Number"
                  as="span"
                  className="font-heading text-[clamp(80px,12vw,180px)] font-bold leading-[0.85] tracking-[-0.04em] text-[rgb(var(--ala-secondary))]/25 [&_p]:text-[rgb(var(--ala-secondary))]/25"
                />
                <div className="flex flex-col gap-6">
                  <RichTextContent
                    path="aboutcontent.journey_2_title"
                    label="Journey 2 Title"
                    as="h3"
                    className="font-heading text-[clamp(24px,2.3vw,40px)] font-medium leading-[1.15] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
                  />
                  <RichTextContent
                    path="aboutcontent.journey_2_body"
                    label="Journey 2 Body"
                    as="p"
                    className="max-w-[640px] font-heading text-[clamp(16px,1.3vw,20px)] font-normal leading-[1.55] tracking-[-0.2px] text-[rgb(var(--ala-on-primary))] [&_p]:text-[rgb(var(--ala-on-primary))]"
                  />
                </div>
              </motion.div>
            ) : null}
            {index === 2 ? (
              <motion.div
                key="journey-2"
                custom={direction}
                initial={{ opacity: 0, x: direction > 0 ? 60 : -60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction > 0 ? -60 : 60 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[clamp(120px,16vw,200px)_1fr] lg:gap-16"
              >
                <RichTextContent
                  path="aboutcontent.journey_3_num"
                  label="Journey 3 Number"
                  as="span"
                  className="font-heading text-[clamp(80px,12vw,180px)] font-bold leading-[0.85] tracking-[-0.04em] text-[rgb(var(--ala-secondary))]/25 [&_p]:text-[rgb(var(--ala-secondary))]/25"
                />
                <div className="flex flex-col gap-6">
                  <RichTextContent
                    path="aboutcontent.journey_3_title"
                    label="Journey 3 Title"
                    as="h3"
                    className="font-heading text-[clamp(24px,2.3vw,40px)] font-medium leading-[1.15] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
                  />
                  <RichTextContent
                    path="aboutcontent.journey_3_body"
                    label="Journey 3 Body"
                    as="p"
                    className="max-w-[640px] font-heading text-[clamp(16px,1.3vw,20px)] font-normal leading-[1.55] tracking-[-0.2px] text-[rgb(var(--ala-on-primary))] [&_p]:text-[rgb(var(--ala-on-primary))]"
                  />
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex gap-3">
            <button
              type="button"
              aria-label="Previous"
              onClick={() => goTo(index - 1)}
              className="flex h-[52px] w-[52px] items-center justify-center rounded-full border border-[rgb(var(--ala-on-primary))]/20 transition-colors hover:bg-[rgb(var(--ala-on-primary))]/10"
            >
              <ArrowLeft className="h-5 w-5 text-[rgb(var(--ala-on-primary))]" />
            </button>
            <button
              type="button"
              aria-label="Next"
              onClick={() => goTo(index + 1)}
              className="flex h-[52px] w-[52px] items-center justify-center rounded-full border border-[rgb(var(--ala-on-primary))]/20 transition-colors hover:bg-[rgb(var(--ala-on-primary))]/10"
            >
              <ArrowRight className="h-5 w-5 text-[rgb(var(--ala-on-primary))]" />
            </button>
          </div>

          <div className="flex min-w-[160px] max-w-[420px] flex-1 gap-2">
            {[0, 1, 2].map((i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => goTo(i)}
                className={`h-[3px] flex-1 rounded-full transition-colors ${
                  i <= index ? 'bg-[rgb(var(--ala-secondary))]' : 'bg-[rgb(var(--ala-on-primary))]/15'
                }`}
              />
            ))}
          </div>

          <span className="font-heading text-sm font-medium tabular-nums tracking-[-0.3px] text-[rgb(var(--ala-on-primary))]/50">
            {String(index + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
          </span>
        </div>
      </div>
    </section>
  );
}

export function AboutContent() {
  const image = useSlotGroup('aboutcontent.image', imageDef);
  const cta = useSlotGroup('aboutcontent.cta', ctaDef);
  const statsBg = useSlotGroup('aboutcontent.stats_bg', statsBgDef);
  const map = useSlotGroup('aboutcontent.map', mapDef);
  const badgeValue = plainText(useContentToken('aboutcontent.badge_value'));
  const marqueeText =
    plainText(useContentToken('aboutcontent.marquee')) ||
    'Building Communities. Creating Opportunity.';
  const fromLabel = plainText(useContentToken('aboutcontent.from_label')) || 'From';
  const fromYear = plainText(useContentToken('aboutcontent.from_year')) || '2016';
  const toLabel = plainText(useContentToken('aboutcontent.to_label')) || 'To';
  const toNow = plainText(useContentToken('aboutcontent.to_now')) || 'Now';
  const stat1 = plainText(useContentToken('aboutcontent.stat_1_value'));
  const stat2 = plainText(useContentToken('aboutcontent.stat_2_value'));
  const stat3 = plainText(useContentToken('aboutcontent.stat_3_value'));
  const stat4 = plainText(useContentToken('aboutcontent.stat_4_value'));

  const timelineRef = useRef<HTMLElement>(null);
  const { scrollYProgress: timelineProgress } = useScroll({
    target: timelineRef,
    offset: ['start start', 'end end'],
  });
  const timelineX = useTransform(timelineProgress, [0.62, 1], ['0%', '-150%']);
  const timelineOpacity = useTransform(timelineProgress, [0.6, 0.95], [1, 0]);

  const mapRef = useRef<HTMLElement>(null);
  const { scrollYProgress: mapProgress } = useScroll({
    target: mapRef,
    offset: ['start end', 'center center'],
  });
  const mapScale = useTransform(mapProgress, [0, 1], [0.12, 1]);
  const mapOpacity = useTransform(mapProgress, [0, 0.5], [0, 1]);

  return (
    <>
      {/* From 2016 → Now sticky timeline */}
      <section ref={timelineRef} className="relative w-full bg-[rgb(var(--ala-background))]" style={{ height: '300vh' }}>
        <div className="sticky top-0 flex h-screen w-full flex-col justify-center overflow-clip">
          <motion.div
            style={{ x: timelineX, opacity: timelineOpacity }}
            className="mx-auto w-full max-w-[1440px] px-6 md:px-[clamp(16px,6.1vw,88px)]"
          >
            <div className="relative flex w-full flex-col">
              <EditableToken path="aboutcontent.from_label" type="text" label="From Label" as="div" className="self-start">
                <ScrollWord progress={timelineProgress} range={[0, 0.18]} className="self-start">
                  {fromLabel}
                </ScrollWord>
              </EditableToken>
              <EditableToken path="aboutcontent.from_year" type="text" label="From Year" as="div" className="pl-[15%] md:pl-[clamp(38px,17.4vw,250px)]">
                <ScrollWord
                  progress={timelineProgress}
                  range={[0.14, 0.32]}
                  className="pl-0"
                >
                  {fromYear}
                </ScrollWord>
              </EditableToken>
              <EditableToken path="aboutcontent.to_label" type="text" label="To Label" as="div" className="pl-[30%] md:pl-[clamp(75px,34.7vw,500px)]">
                <ScrollWord progress={timelineProgress} range={[0.28, 0.46]} className="pl-0">
                  {toLabel}
                </ScrollWord>
              </EditableToken>
              <EditableToken path="aboutcontent.to_now" type="text" label="To Now" as="div" className="pl-[40%] md:pl-[clamp(90px,41.7vw,600px)]">
                <ScrollWord
                  progress={timelineProgress}
                  range={[0.42, 0.6]}
                  className="pl-0 text-[rgb(var(--ala-text-muted))]"
                >
                  {toNow}
                </ScrollWord>
              </EditableToken>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission + image */}
      <section className="mx-auto w-full max-w-[1440px] bg-[rgb(var(--ala-background))] px-6 pb-8 md:px-[clamp(16px,6.7vw,96px)] md:pb-[clamp(20px,4vw,64px)]">
        <div className="grid grid-cols-1 items-start gap-16 lg:grid-cols-[1.15fr_1fr] lg:gap-[clamp(26px,9.2vw,132px)]">
          <motion.div
            initial={{ opacity: 0, y: 80 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col"
          >
            <div className="mb-8 border-b border-[rgba(10,17,25,0.10)] pb-8">
              <RichTextContent
                path="aboutcontent.eyebrow"
                label="Eyebrow"
                as="span"
                className="mb-6 block font-heading text-base font-normal uppercase tracking-wider text-[rgb(var(--ala-text-muted))]"
              />
              <RichTextContent
                path="aboutcontent.headline"
                label="Headline"
                as="h2"
                className="font-heading text-[clamp(33px,4.17vw,60px)] font-medium leading-[1.0833] text-[rgb(var(--ala-text))]"
              />
            </div>
            <RichTextContent
              path="aboutcontent.body"
              label="Body"
              as="div"
              className="mb-12 max-w-[638px] space-y-6 font-heading text-lg font-normal leading-[1.5] text-[rgb(var(--ala-text-muted))] [&_p]:mb-0"
            />
            <EditableSlotGroup groupPath="aboutcontent.cta" definition={ctaDef}>
              <a
                href={cta.url || '/investors'}
                target={cta._target || '_self'}
                className="group inline-flex w-fit items-center gap-2 rounded-full bg-[rgb(var(--ala-secondary))] px-5 py-[15px] font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-secondary))] transition-colors hover:bg-[#3a85d0]"
              >
                <span>{cta.label || 'explore our approach'}</span>
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </a>
            </EditableSlotGroup>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 120 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto w-full max-w-[600px] lg:mx-0"
          >
            <EditableSlotGroup groupPath="aboutcontent.image" definition={imageDef}>
              <div className="relative aspect-[550/607] w-full overflow-clip rounded-[10px] shadow-xl">
                <VibesitesImage
                  url={
                    image.image ||
                    ''
                  }
                  alt={image.alt || ''}
                  sizes="(min-width: 1024px) 40vw, 100vw"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
            </EditableSlotGroup>
            <div className="absolute -bottom-6 -right-6 z-10 flex w-[140px] flex-col items-center justify-center rounded-[10px] bg-[rgb(var(--ala-secondary))] p-4 shadow-2xl md:bottom-[10%] md:-right-[10%] md:w-[180px] md:p-[30px]">
              <EditableToken
                path="aboutcontent.badge_value"
                type="text"
                label="Badge Value"
                as="span"
                className="font-heading text-[clamp(32px,3.33vw,48px)] font-bold leading-[1.2] text-[rgb(var(--ala-on-secondary))]"
              >
                {badgeValue || '36k+'}
              </EditableToken>
              <RichTextContent
                path="aboutcontent.badge_label"
                label="Badge Label"
                as="span"
                className="mt-1 text-center font-heading text-sm font-normal text-[rgb(var(--ala-on-secondary))] md:text-base"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <section className="w-full overflow-clip bg-[rgb(var(--ala-surface))] pb-12 pt-4 md:pb-24 md:pt-8">
        <EditableToken
          path="aboutcontent.marquee"
          type="text"
          label="Marquee Text"
          as="div"
          className="sr-only"
        >
          {marqueeText}
        </EditableToken>
        <div className="relative flex whitespace-nowrap" aria-hidden>
          <motion.div
            className="flex items-center gap-8 pr-8"
            animate={{ x: ['0%', '-50%'] }}
            transition={{ ease: 'linear', duration: 45, repeat: Infinity }}
          >
            {Array.from({ length: 4 }).map((_, i) => (
              <h2
                key={i}
                className="font-heading text-[clamp(40px,7.64vw,110px)] font-semibold text-[#D1D5DB]"
              >
                {marqueeText}
              </h2>
            ))}
          </motion.div>
        </div>
      </section>

      <JourneyCarousel />

      {/* US Map */}
      <section
        ref={mapRef}
        className="mx-auto flex w-full max-w-[1440px] justify-center bg-[rgb(var(--ala-background))] px-6 py-20 md:px-[clamp(16px,6.7vw,96px)] md:py-[clamp(40px,11.1vw,160px)]"
      >
        <motion.div
          style={{ scale: mapScale, opacity: mapOpacity }}
          className="relative aspect-[1004/647] w-full max-w-[1180px] origin-center"
        >
          <EditableSlotGroup groupPath="aboutcontent.map" definition={mapDef}>
            <VibesitesImage
              url={!map.image || map.image === MAP_DEFAULT_PATH ? aboutUsMapSvg : map.image}
              alt={map.alt || 'American Landmark communities across the United States'}
              sizes="(min-width: 1024px) 1180px, 100vw"
              className="absolute inset-0 h-full w-full object-contain"
            />
          </EditableSlotGroup>
          <MapMarkers />
        </motion.div>
      </section>

      {/* By the Numbers */}
      <section className="relative w-full overflow-clip bg-[rgb(var(--ala-dark))] px-6 py-20 md:px-8 md:py-28">
        <EditableSlotGroup groupPath="aboutcontent.stats_bg" definition={statsBgDef}>
          <div className="absolute inset-0 z-0" aria-hidden>
            <VibesitesImage
              url={
                statsBg.image ||
                ''
              }
              alt={statsBg.alt || ''}
              sizes="100vw"
              className="h-full w-full object-cover opacity-25 mix-blend-overlay"
            />
          </div>
        </EditableSlotGroup>

        <div className="relative z-10 mx-auto flex w-full max-w-[1180px] flex-col items-center gap-12 md:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            className="flex flex-col items-center gap-4 text-center"
          >
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded bg-[rgb(var(--ala-accent))]" aria-hidden />
              <RichTextContent
                path="aboutcontent.impact_eyebrow"
                label="Impact Eyebrow"
                as="span"
                className="font-heading text-sm font-medium uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-primary))]/60"
              />
            </div>
            <RichTextContent
              path="aboutcontent.impact_headline"
              label="Impact Headline"
              as="h2"
              className="font-heading text-[clamp(33px,3.69vw,60px)] font-medium leading-[1.1] tracking-[-0.05em] text-[rgb(var(--ala-on-primary))]"
            />
          </motion.div>

          <div className="grid w-full grid-cols-1 border-t border-[rgb(var(--ala-on-primary))]/15 md:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col gap-8 border-b border-[rgb(var(--ala-on-primary))]/15 px-6 pb-12 pt-10 md:border-r md:border-[rgb(var(--ala-on-primary))]/15 md:px-8 lg:border-b-0 lg:border-r lg:border-[rgb(var(--ala-on-primary))]/15">
              <div className="relative">
                <span className="sr-only">
                  <RichTextContent path="aboutcontent.stat_1_value" label="Stat 1 Value" as="span" />
                </span>
                <CountUp
                  value={stat1 || '0'}
                  className="font-heading text-[clamp(32px,3.4vw,52px)] font-medium leading-[1.1] tracking-[-0.04em] text-[rgb(var(--ala-on-primary))]"
                />
              </div>
              <RichTextContent
                path="aboutcontent.stat_1_label"
                label="Stat 1 Label"
                as="p"
                className="max-w-[220px] font-heading text-lg font-medium leading-snug tracking-[-0.3px] text-[rgb(var(--ala-on-primary))] [&_p]:text-[rgb(var(--ala-on-primary))]"
              />
            </div>
            <div className="flex flex-col gap-8 border-b border-[rgb(var(--ala-on-primary))]/15 px-6 pb-12 pt-10 md:px-8 lg:border-b-0 lg:border-r lg:border-[rgb(var(--ala-on-primary))]/15">
              <div className="relative">
                <span className="sr-only">
                  <RichTextContent path="aboutcontent.stat_2_value" label="Stat 2 Value" as="span" />
                </span>
                <CountUp
                  value={stat2 || '0'}
                  className="font-heading text-[clamp(32px,3.4vw,52px)] font-medium leading-[1.1] tracking-[-0.04em] text-[rgb(var(--ala-on-primary))]"
                />
              </div>
              <RichTextContent
                path="aboutcontent.stat_2_label"
                label="Stat 2 Label"
                as="p"
                className="max-w-[220px] font-heading text-lg font-medium leading-snug tracking-[-0.3px] text-[rgb(var(--ala-on-primary))] [&_p]:text-[rgb(var(--ala-on-primary))]"
              />
            </div>
            <div className="flex flex-col gap-8 border-b border-[rgb(var(--ala-on-primary))]/15 px-6 pb-12 pt-10 md:border-r md:border-[rgb(var(--ala-on-primary))]/15 md:px-8 lg:border-b-0 lg:border-r lg:border-[rgb(var(--ala-on-primary))]/15">
              <div className="relative">
                <span className="sr-only">
                  <RichTextContent path="aboutcontent.stat_3_value" label="Stat 3 Value" as="span" />
                </span>
                <CountUp
                  value={stat3 || '0'}
                  className="font-heading text-[clamp(32px,3.4vw,52px)] font-medium leading-[1.1] tracking-[-0.04em] text-[rgb(var(--ala-on-primary))]"
                />
              </div>
              <RichTextContent
                path="aboutcontent.stat_3_label"
                label="Stat 3 Label"
                as="p"
                className="max-w-[220px] font-heading text-lg font-medium leading-snug tracking-[-0.3px] text-[rgb(var(--ala-on-primary))] [&_p]:text-[rgb(var(--ala-on-primary))]"
              />
            </div>
            <div className="flex flex-col gap-8 px-6 pb-12 pt-10 md:px-8">
              <div className="relative">
                <span className="sr-only">
                  <RichTextContent path="aboutcontent.stat_4_value" label="Stat 4 Value" as="span" />
                </span>
                <CountUp
                  value={stat4 || '0'}
                  className="font-heading text-[clamp(32px,3.4vw,52px)] font-medium leading-[1.1] tracking-[-0.04em] text-[rgb(var(--ala-on-primary))]"
                />
              </div>
              <RichTextContent
                path="aboutcontent.stat_4_label"
                label="Stat 4 Label"
                as="p"
                className="max-w-[220px] font-heading text-lg font-medium leading-snug tracking-[-0.3px] text-[rgb(var(--ala-on-primary))] [&_p]:text-[rgb(var(--ala-on-primary))]"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AboutContent',
  displayName: 'About Content',
  tokens: {
    'aboutcontent.from_label': { label: 'From Label', type: 'text', defaultValue: 'From' },
    'aboutcontent.from_year': { label: 'From Year', type: 'text', defaultValue: '2016' },
    'aboutcontent.to_label': { label: 'To Label', type: 'text', defaultValue: 'To' },
    'aboutcontent.to_now': { label: 'To Now', type: 'text', defaultValue: 'Now' },
    'aboutcontent.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Since 2016</p>',
    },
    'aboutcontent.headline': {
      label: 'Headline',
      type: 'richtext',
      defaultValue:
        '<p>Every community has a story. Ours begins with a commitment to seeing potential where others see possibility.</p>',
    },
    'aboutcontent.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        "<p>Founded in 2016, American Landmark Apartments was built on more than 25 years of multifamily expertise following the successful sale of Landmark Apartment Trust. Since then, we've grown into one of the nation's leading owner-operators of multifamily communities, investing in high-growth Sunbelt markets while staying true to one simple belief: great communities create lasting value.</p><p>Today, we own and operate approximately 36,000 apartment homes representing more than $6 billion in gross assets under management. But our greatest achievement isn't measured in numbers - it's measured in the people who call our communities home, the careers we've helped build, and the neighborhoods we've helped strengthen.</p>",
    },
    'aboutcontent.badge_value': { label: 'Badge Value', type: 'text', defaultValue: '36k+' },
    'aboutcontent.badge_label': {
      label: 'Badge Label',
      type: 'richtext',
      defaultValue: '<p>Communities</p>',
    },
    'aboutcontent.marquee': {
      label: 'Marquee Text',
      type: 'text',
      defaultValue: 'Building Communities. Creating Opportunity.',
    },
    'aboutcontent.journey_eyebrow': {
      label: 'Journey Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Our Journey</p>',
    },
    'aboutcontent.journey_headline': {
      label: 'Journey Headline',
      type: 'richtext',
      defaultValue: '<p>Decades of multifamily excellence, built step by step.</p>',
    },
    'aboutcontent.journey_1_num': {
      label: 'Journey 1 Number',
      type: 'richtext',
      defaultValue: '<p>01</p>',
    },
    'aboutcontent.journey_1_title': {
      label: 'Journey 1 Title',
      type: 'richtext',
      defaultValue: '<p>A Strong Foundation</p>',
    },
    'aboutcontent.journey_1_body': {
      label: 'Journey 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>Our leadership team entered 2016 with decades of multifamily experience and a proven history of transforming communities through thoughtful investment, operational excellence, and resident-focused management.</p>',
    },
    'aboutcontent.journey_2_num': {
      label: 'Journey 2 Number',
      type: 'richtext',
      defaultValue: '<p>02</p>',
    },
    'aboutcontent.journey_2_title': {
      label: 'Journey 2 Title',
      type: 'richtext',
      defaultValue: '<p>Purposeful Growth</p>',
    },
    'aboutcontent.journey_2_body': {
      label: 'Journey 2 Body',
      type: 'richtext',
      defaultValue:
        "<p>Rather than pursuing growth for growth's sake, we've strategically expanded throughout the Sunbelt, identifying markets with long-term opportunity and communities with untapped potential.</p>",
    },
    'aboutcontent.journey_3_num': {
      label: 'Journey 3 Number',
      type: 'richtext',
      defaultValue: '<p>03</p>',
    },
    'aboutcontent.journey_3_title': {
      label: 'Journey 3 Title',
      type: 'richtext',
      defaultValue: '<p>Creating Places People Love</p>',
    },
    'aboutcontent.journey_3_body': {
      label: 'Journey 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>Every renovation, amenity upgrade, and resident experience is designed with one goal in mind—to create communities where people genuinely enjoy living while delivering long-term value for our investors and partners.</p>',
    },
    'aboutcontent.impact_eyebrow': {
      label: 'Impact Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Our Impact</p>',
    },
    'aboutcontent.impact_headline': {
      label: 'Impact Headline',
      type: 'richtext',
      defaultValue: '<p>By the Numbers</p>',
    },
    'aboutcontent.stat_1_value': {
      label: 'Stat 1 Value',
      type: 'richtext',
      defaultValue: '<p>$6B+</p>',
    },
    'aboutcontent.stat_1_label': {
      label: 'Stat 1 Label',
      type: 'richtext',
      defaultValue: '<p>Gross Assets Under Management</p>',
    },
    'aboutcontent.stat_2_value': {
      label: 'Stat 2 Value',
      type: 'richtext',
      defaultValue: '<p>36,000+</p>',
    },
    'aboutcontent.stat_2_label': {
      label: 'Stat 2 Label',
      type: 'richtext',
      defaultValue: '<p>Apartment Homes Across the Sunbelt</p>',
    },
    'aboutcontent.stat_3_value': {
      label: 'Stat 3 Value',
      type: 'richtext',
      defaultValue: '<p>25+ Years</p>',
    },
    'aboutcontent.stat_3_label': {
      label: 'Stat 3 Label',
      type: 'richtext',
      defaultValue: '<p>Multifamily Leadership Experience</p>',
    },
    'aboutcontent.stat_4_value': {
      label: 'Stat 4 Value',
      type: 'richtext',
      defaultValue: '<p>7 States</p>',
    },
    'aboutcontent.stat_4_label': {
      label: 'Stat 4 Label',
      type: 'richtext',
      defaultValue: '<p>Serving Residents Across High-Growth Markets</p>',
    },
  },
  slotGroups: {
    'aboutcontent.image': imageDef,
    'aboutcontent.cta': ctaDef,
    'aboutcontent.stats_bg': statsBgDef,
    'aboutcontent.map': mapDef,
  },
};

(AboutContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AboutContent;
